import type { RootState } from "../../redux";
import { Subdomains, UserEndpoints } from "../../constants";
import { authenticateUser, registerUser } from "../../functions";
import type { RegisterData, AuthenticateData, ImageCaptureState } from "../../types";
import { isAxiosError } from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const initialState: ImageCaptureState = {
  paneemError: null,
  attempts: 0,
  askDocumentBack: false,
};

export const registerAction = createAsyncThunk(
  `${Subdomains.users}${UserEndpoints.registration}`,
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await registerUser(data);
      const payload = response.data;

      return payload;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.data.message);
      else rejectWithValue(error.message);
    }
  }
);

export const authenticateAction = createAsyncThunk(
  `${Subdomains.users}${UserEndpoints.authentication}`,
  async (data: AuthenticateData, { rejectWithValue }) => {
    try {
      const response = await authenticateUser(data);

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) return rejectWithValue(error.response!.data.message);
      else rejectWithValue(error.message);
    }
  }
);

export const imageCapture = createSlice({
  name: "imageCapture",
  initialState,
  reducers: {
    setPaneemError: (state, { payload }: { payload: null | 1 | 2 }) => {
      state.paneemError = payload;
    },

    incrementAttempts: (state) => {
      state.attempts += 1;
    },

    overrideAttempts: (state, { payload }: { payload: number }) => {
      state.attempts = payload;
    },

    setAskDocumentBack: (state, { payload }: { payload: boolean }) => {
      state.askDocumentBack = payload;
    },

    resetImageCaptureState: () => initialState,
  },

  extraReducers(builder) {
    builder.addCase(registerAction.fulfilled, (state, { payload }) => {
      if (payload!.message === "document_back") state.askDocumentBack = true;
    });

    builder.addCase(registerAction.rejected, (state) => {
      state.paneemError = 1;
    });

    builder.addCase(authenticateAction.rejected, (state) => {
      state.paneemError = 1;
    });
  },
});

export const { setPaneemError, incrementAttempts, overrideAttempts, setAskDocumentBack, resetImageCaptureState } =
  imageCapture.actions;

export const selectPaneemError = (state: RootState) => state.imageCapture.paneemError;
export const selectAttempts = (state: RootState) => state.imageCapture.attempts;
export const selectAskDocumentBack = (state: RootState) => state.imageCapture.askDocumentBack;

export default imageCapture.reducer;
